import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import { global } from "@shared/libs/Global";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { showPopupModal } from "@widgets/CommonModal";
import SN_TRA_IBL_REG_004_POP_01 from "./SN_TRA_IBL_REG_004_POP_01";
import SearchPanel from "@widgets/SearchPanel";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnTab from "@shared/components/SnTab";
import { SnButton } from "@shared/components/SnButton";

interface Props {
  Menuprops: menuInfo;
}

// 미착정산
const SN_TRA_IBL_REG_004 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  const onSearch = async () => {
    try {
      const searchInfo = searchRef.current.getFormdata();
      const isSettled = searchInfo.cd_pro === "Y";
      const params = { id: "sn_tra_ibl_reg_004_01", params: searchInfo };

      const data: any = await fnGetDataSet(params);
      // 미처리: 정산금액/단위원가는 아직 없으므로 비워서 표시하고 원값은 am_src/um_src 에 보관(정산처리 팝업에서 사용)
      // 처리: 정산금액/단위원가 = 금액+배부금액 기준으로 표시
      const grid2 = (data[1] || []).map((r: any) => {
        if (!isSettled)
          return { ...r, am_src: r.am, am: null, um_src: r.um, um: null };
        const am = Math.floor(Number(r.am_k ?? 0) + Number(r.am_dist ?? 0));
        const qt = Number(r.qt ?? 0);
        return { ...r, am, um: qt !== 0 ? Math.floor(am / qt) : 0 };
      });
      // 부대비용정보: 미처리는 3번째(no_io 기준), 처리는 4번째(no_pur 기준) 결과셋
      setDataSources({
        grid1: data[0] || [],
        grid2,
        grid3: (isSettled ? data[3] : data[2]) || [],
      });
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    const selectedRow =
      gridRefs.current["grid1"].current.getSelectedRows()?.[0];
    if (!selectedRow) {
      await showDialog("alter", "선택된 행이 없습니다.");
      return;
    }

    const searchInfo = searchRef.current.getFormdata();
    if (id == "btnApply") {
      if (searchInfo.cd_pro === "Y") {
        await showDialog("alter", "이미 정산처리 되었습니다.");
        return;
      }
      if (!selectedRow.dt_pur) {
        await showDialog("alter", "정산일자 를 입력하시기 바랍니다.");
        return;
      }

      // 화면에서 비운 정산금액(am)/단위원가(um)는 원값(am_src/um_src)으로 되돌려 팝업에 넘긴다
      const grid2clone: any[] = [];
      gridRefs.current["grid2"].current.forEachFilteredNode((node: any) => {
        grid2clone.push({ ...node.data, am: node.data.am_src, um: node.data.um_src });
      });
      const sum = (key: string) =>
        grid2clone.reduce((s, r) => s + Number(r[key] ?? 0), 0);

      const component = (
        <SN_TRA_IBL_REG_004_POP_01
          Grid1SelectedData={selectedRow}
          footerdata={{ am_k_footer: sum("am_k"), am_footer: sum("am") }}
          clonedata={grid2clone}
        />
      );
      const result = await showPopupModal({
        title: "정산처리",
        children: component,
        height: "75%",
        width: "80%",
        okayBtn: true,
        validation: true,
        checkData: false,
      });
      if (!result || result === "close" || result === "e") return;

      searchRef.current.setFormdata({ ...searchInfo, cd_pro: "Y" });
      await onSearch();
    }

    if (id == "btnCancel") {
      if (searchInfo.cd_pro === "N") {
        await showDialog("alter", "정산 미처리 건입니다.");
        return;
      }

      const ok = await fnSave("SN_TRA_IBL_REG_004_CANCEL_01", {
        cd_firm: userInfo.cd_firm,
        no_pur: selectedRow.no_pur,
      });
      if (!ok) return;

      // 정산취소 후 정산여부를 '미처리'로 바꾸고 재조회
      searchRef.current.setFormdata({ ...searchInfo, cd_pro: "N" });
      await onSearch();
    }
  };

  // 정산여부 변경 시 자동 재조회
  const onSearchValueChanged = (event: any) => {
    if (event?.colId === "cd_pro") onSearch();
  };

  const onFocusedRowChanged = (row: any) => {
    if (!row) return;
    gridRefs.current["grid2"].current.applyFilters({ no_io: row.no_io });
    // 부대비용정보 필터 키: 처리는 매입번호(no_pur), 미처리는 입고번호(no_io)
    const isSettled = searchRef.current.getFormdata().cd_pro === "Y";
    gridRefs.current["grid3"].current.applyFilters(
      isSettled ? { no_pur: row.no_pur } : { no_io: row.no_io }
    );
    gridRefs.current["grid2"].current.selectAndFocusRow(0);
    gridRefs.current["grid3"].current.selectAndFocusRow(0);
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: { onFocusedRowChanged },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {},
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {},
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchValueChanged}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="49.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              isEditable: true,
              rowSelection: "single",
              title: "미착정산",
            }}
            events={grids.grid1.events}
            header={
              <div className="relative flex space-x-3">
                <SnButton
                  text="정산처리"
                  onClick={() => {
                    onGrid1BtnsClick("btnApply");
                  }}
                />
                <SnButton
                  text="정산취소"
                  onClick={() => {
                    onGrid1BtnsClick("btnCancel");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="49.5%">
          <SnTab
            titles={["수입품정보", "부대비용정보"]}
            width={"100%"}
            body={[
              <div className="flex-1 h-full w-full">
                <SnGrid
                  ref={gridRefs.current["grid2"]}
                  columnConfig={grids.grid2.columns}
                  rowData={grids.grid2.dataSource}
                  etcProps={{ isEditable: false, rowSelection: "single" }}
                  events={grids.grid2.events}
                />
              </div>,
              <div className="flex-1 h-full w-full">
                <SnGrid
                  ref={gridRefs.current["grid3"]}
                  columnConfig={grids.grid3.columns}
                  rowData={grids.grid3.dataSource}
                  etcProps={{ isEditable: false, rowSelection: "single" }}
                  events={grids.grid3.events}
                />
              </div>,
            ]}
          ></SnTab>
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_TRA_IBL_REG_004;
